import { memo, useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, Check, XCircle } from 'lucide-react'
import { type IncomingRequest, type RequestLesson, type LessonResponseItem, teacherApi } from '@/services/api/teacher'
import { FinanceChoiceRow } from '@/components/teacher/FinanceChoiceRow'
import { financeUiEnabled, showSubstitutePayBand } from '@/utils/financeUi'
import { useDirectorEnabled } from '@/hooks/useDirectorEnabled'
import { extractErrorMessage } from '@/utils/errorHandler'
import type { BasicCascadePreviewResponse } from '@/types/teacher-request'
import { getStatusColor, getAcceptedStatusLabel, getIncomingBadgeStatus, getBorderLColor } from '@/utils/statusHelpers'
import { getLessonRowStyle } from '@/utils/lessonRowColors'
import { RequestBadges } from './RequestBadges'
import { CascadeIndicator } from './CascadeIndicator'
import { PriorityCascadeIndicator } from './PriorityCascadeIndicator'
import { CascadeLegendTrigger } from './CascadeLegendTrigger'
import { LessonDecisionToggle } from './LessonDecisionToggle'
import { ResponseCountdown } from '../ui/ResponseCountdown'
import { sortLessonsByDateTime } from '@/utils/lessonSort'
import { substitutionRoomLabel } from '@/utils/lessonRoom'
import { lessonDayDateCellClass, lessonDayGroup, lessonDayRowClass } from '@/utils/lessonDayGroup'
import { useAuthStore } from '@/store'
import { onCascadeStateChanged, dispatchCascadeStateChanged } from '@/utils/cascadeEvents'
import { getTakenOverLessonIds } from '@/utils/prepRequestLessons'
import { getPrepPagePath } from '@/utils/prepNavigation'

export interface IncomingRequestCardProps {
  request: IncomingRequest
  onRequestPrep?: (requestId: string, reason?: string, lessonRequestIds?: string[]) => Promise<void>
  onCancel?: (request: IncomingRequest) => void
  /** Per-lesson cancel X: opens the same StornoModal with the lesson pre-selected. */
  onCancelLesson?: (request: IncomingRequest, lessonId: string) => void
  onSendMessage?: (requestId: string) => void
  onResponded?: () => Promise<void> | void
  isHighlighted?: boolean
  isDimmed?: boolean
  onDismissHighlight?: () => void
}

/**
 * HARD_TERMINAL: Status, die nicht mehr geändert werden können (ausser accepted/booked)
 * CANCELABLE_STATES: Status, für die der Storno-Button angezeigt wird
 * 
 * Storno-Button wird angezeigt wenn:
 * - request.status in CANCELABLE_STATES ist
 * - request.already_covered false ist (nicht von jemand anderem übernommen)
 * - onCancel prop übergeben wurde
 * 
 * Per-Lektion-Storno-X wird angezeigt wenn:
 * - request.status nicht in HARD_TERMINAL ist (ausser accepted/booked)
 * - request.already_covered false ist
 * - lesson.status nicht 'cancelled' ist
 * - lesson nicht einem anderen Lehrer zugewiesen ist
 * - lesson.assignment_state 'waiting', 'accepted', oder 'confirmed' ist
 */
const HARD_TERMINAL = ['cancelled', 'expired', 'accepted', 'booked']
const CANCELABLE_STATES = ['accepted', 'partially_filled', 'partially_accepted', 'booked']
const OWN_TABLE_STATES = new Set([
  'pending',
  'waiting',
  'accepted',
  'confirmed',
  'declined',
  'accepted_not_selected',
  'cancelled',
  'timed_out',
])

function isIncomingTableLesson(lesson: RequestLesson, _teacherId?: string): boolean {
  if (lesson.can_respond || lesson.can_un_decline) return true
  if (lesson.assigned_teacher_id) return true
  if (lesson.my_response) return true
  const state = lesson.assignment_state || lesson.status
  if (OWN_TABLE_STATES.has(state || '')) return true
  if (lesson.status === 'cancelled') return true
  return false
}

export const IncomingRequestCard = memo(function IncomingRequestCard({
  request,
  onRequestPrep,
  onCancel,
  onCancelLesson,
  onSendMessage,
  onResponded,
  isHighlighted,
  isDimmed,
  onDismissHighlight,
}: IncomingRequestCardProps) {
  const navigate = useNavigate()
  const user = useAuthStore(state => state.user)
  const displayStatus = getIncomingBadgeStatus(request)
  const statusColor = getStatusColor(displayStatus)
  const statusLabel = getAcceptedStatusLabel(request, user?.teacher_id)

  const [lessons, setLessons] = useState<RequestLesson[]>([])
  const [isLoadingLessons, setIsLoadingLessons] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [cascadePreview, setCascadePreview] = useState<BasicCascadePreviewResponse | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const genRef = useRef(0)

  const [showPrepDialog, setShowPrepDialog] = useState(false)
  const [prepReason, setPrepReason] = useState('')
  const [isRequestingPrep, setIsRequestingPrep] = useState(false)

  const [lessonDecisions, setLessonDecisions] = useState<Record<string, 'interested' | 'decline'>>({})
  const [isSubmittingLessons, setIsSubmittingLessons] = useState(false)
  const [lessonSubmitError, setLessonSubmitError] = useState<string | null>(null)
  const [unDecliningLessonId, setUnDecliningLessonId] = useState<string | null>(null)
  const [isRespondingSingle, setIsRespondingSingle] = useState(false)
  const [selectedDays, setSelectedDays] = useState<Record<string, boolean>>({})
  const [financeOverride, setFinanceOverride] = useState<'paid_out' | 'lesson_account' | null>(null)
  const [financeSaving, setFinanceSaving] = useState(false)
  const [financeError, setFinanceError] = useState<string | null>(null)


  const isMultiLesson = request.lesson_count > 1
  const hardTerminal = HARD_TERMINAL.includes(request.status)
  const isCancelled = request.status === 'cancelled'
  const directLessonIds = useMemo(() => new Set(request.direct_lesson_ids ?? []), [request.direct_lesson_ids])
  const isActionable = !hardTerminal && !request.already_covered && !request.is_direct_assignment

  useEffect(() => {
    setFinanceOverride(null)
  }, [request.id, request.finance_payout])

  const financePersonal = financeOverride ?? request.finance_payout ?? null
  const directorOn = useDirectorEnabled()
  const hrOn = financeUiEnabled(request.hr_module_enabled)
  const showFinanceChoice = showSubstitutePayBand(directorOn, request)

  const handleFinanceSwitch = async (value: string) => {
    if (value !== 'paid_out' && value !== 'lesson_account') return
    setFinanceSaving(true)
    setFinanceError(null)
    const previous = financePersonal
    setFinanceOverride(value)
    try {
      await teacherApi.setSubstitutePayout(request.id, value)
    } catch (err: unknown) {
      setFinanceOverride(previous)
      setFinanceError(extractErrorMessage(err, { loadError: 'Speichern fehlgeschlagen' }))
    } finally {
      setFinanceSaving(false)
    }
  }
  // Also load lessons for cancelled requests so the "Storniert" tab can show details.
  const shouldLoadLessons = isMultiLesson && (isActionable || CANCELABLE_STATES.includes(request.status) || isCancelled)
  const shouldLoadSingleLesson = !isMultiLesson && (CANCELABLE_STATES.includes(request.status) || isCancelled) && !request.already_covered

  const pendingDecisionsCount = Object.keys(lessonDecisions).length

  const visibleLessons = useMemo(
    () => lessons.filter(lesson => isIncomingTableLesson(lesson, user?.teacher_id)),
    [lessons, user?.teacher_id],
  )
  const respondableLessons = visibleLessons.filter(l => l.can_respond)
  const isPackageOffer = respondableLessons.length > 0
    && respondableLessons.every(l => l.block_type === 'full_day' || l.block_type === 'partial')
  const isBrockenOffer = respondableLessons.length > 0
    && respondableLessons.every(l => l.block_type === 'partial')
  const ganztagDates = Array.from(
    new Set(
      respondableLessons
        .filter(l => l.block_type !== 'partial')
        .map(l => l.date)
        .filter(Boolean) as string[],
    ),
  ).sort()

  // Check if any lesson is HR-relevant (confirmed_at or status === 'booked')
  const hasHrRelevantLessons = useMemo(() => {
    return lessons.some(lesson => 
      lesson.confirmed_at || lesson.status === 'booked'
    )
  }, [lessons])

  const loadLessons = useCallback(() => {
    setIsLoadingLessons(true)
    teacherApi.getRequestLessons(request.id)
      .then(data => {
        setLessons(sortLessonsByDateTime(data.lessons))
        setLessonDecisions({})
        setLessonSubmitError(null)
      })
      .catch(() => setLessons([]))
      .finally(() => setIsLoadingLessons(false))
  }, [request.id])

  const reloadLessons = useCallback(() => {
    teacherApi.getRequestLessons(request.id)
      .then(data => setLessons(sortLessonsByDateTime(data.lessons)))
      .catch(() => {})
  }, [request.id])

  useEffect(() => {
    if (!shouldLoadLessons) return
    loadLessons()
  }, [request.id, request.status, shouldLoadLessons, loadLessons])

  useEffect(() => {
    if (!shouldLoadSingleLesson) return
    teacherApi.getRequestLessons(request.id)
      .then(data => setLessons(sortLessonsByDateTime(data.lessons)))
      .catch(() => setLessons([]))
  }, [request.id, request.status, shouldLoadSingleLesson])

  useEffect(() => {
    setCascadePreview(null)
  }, [request.id, request.status])

  useEffect(() => {
    if (!isHovered || cascadePreview) return
    if (!request.cascade_preview_enabled) return
    if (request.is_direct_assignment) return
    if (['cancelled', 'expired', 'filled'].includes(request.status)) return
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    const gen = ++genRef.current
    teacherApi.getIncomingCascadePreview(request.id)
      .then(data => {
        if (gen !== genRef.current) return
        if (data.current_teachers.length > 0 || data.next_teachers.length > 0) {
          setCascadePreview(data)
        }
      })
      .catch(() => {})
    return () => controller.abort()
  }, [isHovered, request.id, request.cascade_preview_enabled, request.is_direct_assignment, request.status, cascadePreview])

  useEffect(() => {
    if (!isHovered || !cascadePreview) return
    if (['accepted', 'filled'].includes(request.status) || request.already_covered) return
    const interval = setInterval(() => {
      const gen = ++genRef.current
      teacherApi.getIncomingCascadePreview(request.id)
        .then(data => {
          if (gen !== genRef.current) return
          if (data.current_teachers.length > 0 || data.next_teachers.length > 0) {
            setCascadePreview(data)
          }
        })
        .catch(() => {})
    }, 30000)
    return () => clearInterval(interval)
  }, [isHovered, request.id, request.status, request.already_covered, cascadePreview])

  useEffect(() => {
    return onCascadeStateChanged((eventId) => {
      if (eventId && eventId !== request.id) return
      setCascadePreview(null)
      if (shouldLoadLessons) loadLessons()
      if (shouldLoadSingleLesson) reloadLessons()
    })
  }, [request.id, shouldLoadLessons, shouldLoadSingleLesson, loadLessons, reloadLessons])

  const isPast = request.date && new Date(request.date) < new Date(new Date().toDateString())

  const prepSummary = request.prep_summary
  const prepStatus: 'no_prep' | 'partial' | 'complete' | 'prep_requested' = prepSummary?.has_open_prep_request
    ? 'prep_requested'
    : prepSummary?.has_prep && (prepSummary.lessons_with_prep >= (prepSummary.total_lessons || 1))
      ? 'complete'
      : prepSummary?.has_prep
        ? 'partial'
        : 'no_prep'

  const showPrepRequestButton = CANCELABLE_STATES.includes(request.status)
    && !prepSummary?.has_open_prep_request
    && prepStatus !== 'complete'
    && prepStatus !== 'prep_requested'
    && !!onRequestPrep

  const showPrepViewButton = (CANCELABLE_STATES.includes(request.status) || !!prepSummary?.has_prep)
    && !showPrepDialog

  // Konzept A: Prep-Badge für das Band (statt Header-Badges) — «angefragt»,
  // «X/Y Prep» oder «Kein Prep», je nach Stand.
  const showPrepStatusBadge = prepSummary?.has_open_prep_request
    || !!prepSummary?.has_prep
    || CANCELABLE_STATES.includes(request.status)
  const showPrepBand = showPrepStatusBadge || showPrepRequestButton || showPrepViewButton

  const borderColor = request.already_covered
    ? 'border-l-gray-400'
    : getBorderLColor(displayStatus)

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Datum offen'
    return new Date(dateStr).toLocaleDateString('de-DE', {
      weekday: 'short', day: 'numeric', month: 'long', year: 'numeric',
    })
  }

  const takenOverLessonIds = useMemo(
    () => getTakenOverLessonIds(lessons, user?.teacher_id),
    [lessons, user?.teacher_id],
  )

  const handlePrepRequestClick = () => {
    setShowPrepDialog(true)
  }

  const handleSubmitPrepRequest = async () => {
    if (!onRequestPrep) return
    setIsRequestingPrep(true)
    try {
      await onRequestPrep(
        request.id,
        prepReason || undefined,
        takenOverLessonIds.length > 0 ? takenOverLessonIds : undefined,
      )
      setShowPrepDialog(false)
      setPrepReason('')
    } finally {
      setIsRequestingPrep(false)
    }
  }

  const toggleLessonDecision = useCallback((lessonId: string, decision: 'interested' | 'decline') => {
    setLessonDecisions(prev => {
      if (prev[lessonId] === decision) {
        const next = { ...prev }
        delete next[lessonId]
        return next
      }
      return { ...prev, [lessonId]: decision }
    })
  }, [])

  const handleToggleChoose = useCallback((lessonId: string, decision: 'interested' | 'decline' | null) => {
    if (decision === null) {
      setLessonDecisions(prev => {
        const next = { ...prev }
        delete next[lessonId]
        return next
      })
    } else {
      toggleLessonDecision(lessonId, decision)
    }
  }, [toggleLessonDecision])

  const setAllDecisions = useCallback((decision: 'interested' | 'decline') => {
    const respondable = visibleLessons.filter(l => l.can_respond)
    const decisions: Record<string, 'interested' | 'decline'> = {}
    respondable.forEach(l => { decisions[l.id] = decision })
    setLessonDecisions(decisions)
  }, [visibleLessons])

  const submitLessonResponses = async (
    items: LessonResponseItem[],
  ) => {
    if (items.length === 0) return
    setIsSubmittingLessons(true)
    setLessonSubmitError(null)
    try {
      const result = await teacherApi.respondToLessons(request.id, items)
      setLessonDecisions({})
      loadLessons()
      await onResponded?.()
      dispatchCascadeStateChanged(request.id)
      if (!result.success) {
        setLessonSubmitError(result.message || 'Einige Lektionen konnten nicht verarbeitet werden')
      }
    } catch (err: unknown) {
      setLessonSubmitError((err as { detail?: string })?.detail || 'Fehler beim Verarbeiten der Lektionen')
    } finally {
      setIsSubmittingLessons(false)
    }
  }

  const handleAcceptAllOffered = async () => {
    if (isBrockenOffer) return
    const respondable = visibleLessons.filter(l => l.can_respond && l.block_type !== 'partial')
    await submitLessonResponses(respondable.map(l => ({
      lesson_request_id: l.id,
      response: 'accept',
    })))
  }

  const handleUebernehmenSelectedDays = async () => {
    const dates = Object.entries(selectedDays).filter(([, on]) => on).map(([d]) => d)
    if (dates.length === 0) return
    const respondable = visibleLessons.filter(
      l => l.can_respond && l.block_type !== 'partial' && l.date && dates.includes(l.date),
    )
    await submitLessonResponses(respondable.map(l => ({
      lesson_request_id: l.id,
      response: 'accept',
    })))
    setSelectedDays({})
  }

  const handleDeclineDay = async (date: string) => {
    const respondable = visibleLessons.filter(l => l.can_respond && l.date === date)
    await submitLessonResponses(respondable.map(l => ({
      lesson_request_id: l.id,
      response: 'decline',
    })))
  }

  const handleLessonsSubmit = async () => {
    const entries = Object.entries(lessonDecisions)
    if (entries.length === 0) return
    setIsSubmittingLessons(true)
    setLessonSubmitError(null)
    try {
      const responses: LessonResponseItem[] = entries.map(([lesson_request_id, response]) => ({
        lesson_request_id,
        response,
      }))
      const result = await teacherApi.respondToLessons(request.id, responses)
      setLessonDecisions({})
      loadLessons()
      await onResponded?.()
      dispatchCascadeStateChanged(request.id)
      if (!result.success) {
        setLessonSubmitError(result.message || 'Einige Lektionen konnten nicht verarbeitet werden')
      }
    } catch (err: unknown) {
      setLessonSubmitError((err as { detail?: string })?.detail || 'Fehler beim Verarbeiten der Lektionen')
    } finally {
      setIsSubmittingLessons(false)
    }
  }

  const handleUnDecline = async (lessonId: string) => {
    setUnDecliningLessonId(lessonId)
    setLessonSubmitError(null)
    try {
      await teacherApi.unDeclineLesson(request.id, lessonId)
      loadLessons()
      await onResponded?.()
      dispatchCascadeStateChanged(request.id)
    } catch {
      setLessonSubmitError('Ablehnung konnte nicht zurückgezogen werden')
    } finally {
      setUnDecliningLessonId(null)
    }
  }

  const handleSingleRespond = async (response: 'accept' | 'interested' | 'decline') => {
    setIsRespondingSingle(true)
    setLessonSubmitError(null)
    try {
      await teacherApi.respondToRequest(request.id, { response })
      await onResponded?.()
      dispatchCascadeStateChanged(request.id)
    } catch (err: unknown) {
      setLessonSubmitError((err as { detail?: string })?.detail || 'Fehler beim Bearbeiten der Anfrage')
    } finally {
      setIsRespondingSingle(false)
    }
  }

  const isLessonCancellable = (lesson: RequestLesson): boolean => {
    if (lesson.status === 'cancelled') return false
    if (directLessonIds.has(lesson.id)) return false
    const myTeacherId = user?.teacher_id
    if (myTeacherId && lesson.assigned_teacher_id && lesson.assigned_teacher_id !== myTeacherId) {
      return false
    }
    return true
  }

  const renderCancelX = (lesson: RequestLesson) => {
    if (!isLessonCancellable(lesson)) return null
    if (!onCancelLesson) return null
    const state = lesson.assignment_state || lesson.status
    const isHrRelevant = lesson.confirmed_at || lesson.status === 'booked'

    return (
      <div className="relative group inline-block">
        <button
          onClick={(e) => {
            e.stopPropagation()
            onCancelLesson(request, lesson.id)
          }}
          className="ml-1.5 p-0.5 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition"
          title={state === 'waiting'
            ? 'Aus dem Wartepool stornieren'
            : 'Lektion stornieren (muss bestätigt werden)'}
          aria-label={`Lektion ${lesson.subject_name || ''} stornieren`}
        >
          <X className="w-3.5 h-3.5" />
        </button>
        {hrOn && isHrRelevant && (
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-yellow-50 border border-yellow-300 rounded shadow-lg text-xs text-yellow-900 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 dark:bg-yellow-900/90 dark:border-yellow-600 dark:text-yellow-100">
            <div className="font-semibold mb-1">⚠️ HR-Warnhinweis</div>
            <div>Diese Lektion würde an das HR für die Vergütung gehen.</div>
            <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px">
              <div className="border-4 border-transparent border-t-yellow-300 dark:border-t-yellow-600"></div>
            </div>
          </div>
        )}
      </div>
    )
  }

  const opacityClass = request.already_covered
    ? 'opacity-50'
    : displayStatus === 'cancelled' || displayStatus === 'expired'
      ? 'opacity-50'
      : displayStatus === 'declined' || displayStatus === 'accepted_not_selected'
        ? 'opacity-70'
        : displayStatus === 'in_progress'
          ? 'opacity-90'
          : isPast
            ? 'opacity-60'
            : ''

  const renderLessonStatus = (lesson: RequestLesson) => {
    const decision = lessonDecisions[lesson.id]
    const isLessonCancelled = lesson.status === 'cancelled' || lesson.assignment_state === 'cancelled'
    const isConfirmed = lesson.assignment_state === 'confirmed'
    const isWaiting = lesson.assignment_state === 'waiting'
    const assignedToOther = !!lesson.assigned_teacher_id && lesson.assigned_teacher_id !== user?.teacher_id
    const isAccepted = (lesson.assignment_state === 'accepted' || lesson.my_response === 'accepted') && !assignedToOther
    const isDeclined = lesson.assignment_state === 'declined' || lesson.my_response === 'declined'
    const isAcceptedNotSelected = lesson.assignment_state === 'accepted_not_selected'
    const isBlocked = assignedToOther && !isAcceptedNotSelected && !isDeclined

    if (isLessonCancelled) {
      return (
        <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400" aria-label="Storniert">
          <XCircle className="w-3.5 h-3.5" /> Storniert
        </span>
      )
    }
    if (isAcceptedNotSelected) {
      return (
        <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400" aria-label="Verloren">
          <XCircle className="w-3.5 h-3.5" /> Verloren
        </span>
      )
    }
    if (isDeclined) {
      return (
        <div className="flex items-center justify-end gap-1.5">
          <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400" aria-label="Abgelehnt">
            <XCircle className="w-3.5 h-3.5" /> Abgelehnt
          </span>
          {lesson.can_un_decline && (
            <button
              type="button"
              onClick={() => handleUnDecline(lesson.id)}
              disabled={unDecliningLessonId === lesson.id}
              className="px-1.5 py-0.5 text-[10px] text-gray-500 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 dark:text-gray-400"
              title="Ablehnung zurückziehen"
            >
              {unDecliningLessonId === lesson.id ? '...' : 'Zurückziehen'}
            </button>
          )}
        </div>
      )
    }
    if (isBlocked) {
      return (
        <span className="text-xs text-gray-400 dark:text-gray-500">
          Belegt{lesson.assigned_teacher_name ? ` (${lesson.assigned_teacher_name})` : ''}
        </span>
      )
    }
    if (directLessonIds.has(lesson.id)) {
      return <span className="text-xs text-gray-500 italic">Direkt zugewiesen</span>
    }
    if (isConfirmed) {
      return (
        <div className="flex items-center justify-end gap-1.5">
          <span className="inline-flex items-center gap-1 text-xs font-bold text-green-700 dark:text-green-400" aria-label="Definitiv gebucht">
            <Check className="w-3.5 h-3.5" /> Definitiv
          </span>
          {renderCancelX(lesson)}
        </div>
      )
    }
    if (isAccepted && !isWaiting) {
      return (
        <div className="flex items-center justify-end gap-1.5">
          <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 dark:text-green-400" aria-label="Angenommen">
            <Check className="w-3.5 h-3.5" />
            Angenommen
          </span>
          {renderCancelX(lesson)}
        </div>
      )
    }
    if (isWaiting) {
      return (
        <div className="flex items-center justify-end gap-1.5">
          <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 dark:text-blue-400" aria-label="Im Wartepool">
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" /></svg>
            Wartepool
          </span>
          {renderCancelX(lesson)}
        </div>
      )
    }
    if (lesson.can_respond && isActionable) {
      const packageHint = isBrockenOffer || lesson.block_type === 'partial'
        ? 'Brocken'
        : 'Ganztag'
      return (
        <div className="flex items-center justify-end gap-1.5">
          <span className="text-[10px] text-gray-400 dark:text-gray-500">{packageHint}</span>
          <LessonDecisionToggle
            decision={decision ?? null}
            onChoose={(d) => handleToggleChoose(lesson.id, d)}
          />
        </div>
      )
    }
    return <span className="text-xs text-gray-400 dark:text-gray-500" aria-label="Offen">Offen</span>
  }

  const showFooterActions = isActionable || hardTerminal || request.already_covered || CANCELABLE_STATES.includes(request.status) || request.status === 'partially_filled'

  // Development-Logging (nur im Dev-Modus)
  if (import.meta.env.DEV) {
    console.log('[IncomingRequestCard]', {
      requestId: request.id,
      status: request.status,
      already_covered: request.already_covered,
      is_direct_assignment: request.is_direct_assignment,
      hardTerminal,
      isActionable,
      showFooterActions,
      cancelableState: CANCELABLE_STATES.includes(request.status),
      hasOnCancel: !!onCancel,
      userTeacherId: user?.teacher_id,
      lessonCount: request.lesson_count,
    })
  }

  return (
    <div
      id={`request-card-${request.id}`}
      className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border-l-4 ${borderColor} p-4 ${opacityClass} transition-all relative ${
        isHighlighted ? 'highlighted-card' : isDimmed ? 'dimmed-card' : ''
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      aria-label={`${request.on_behalf_headline || `Anfrage von ${request.requesting_teacher_name}`} für ${request.subject_name || 'unbekannt'} am ${formatDate(request.date)}`}
      aria-live={isHighlighted ? 'polite' : undefined}
    >
      {isHighlighted && onDismissHighlight && (
        <button
          onClick={onDismissHighlight}
          className="absolute top-2 right-2 p-1 rounded-full bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-700 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition z-10"
          aria-label="Highlight entfernen"
        >
          <X className="w-4 h-4" />
        </button>
      )}
      {isHighlighted && (
        <span className="sr-only">Anfrage hervorgehoben vom Radar</span>
      )}
      {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-900 dark:text-gray-100 truncate">
              {request.on_behalf_headline
                || `${request.requesting_teacher_name} (${request.requesting_teacher_short_code})`}
            </span>
            <span className={`px-2 py-0.5 text-xs rounded-full ${statusColor} shrink-0`}>
              {statusLabel}
            </span>
            {request.cancellation_pending && (
              <span className="px-2 py-0.5 text-xs rounded-full bg-orange-500 text-white font-medium animate-pulse shrink-0">
                Storno offen
              </span>
            )}
            {request.viewer_answer_deadline && (
              <ResponseCountdown deadline={request.viewer_answer_deadline} variant="badge" size="sm" lateAnswerAllowed />
            )}
            <RequestBadges request={request} />
            {request.already_covered && (
              <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 shrink-0">
                Bereits vergeben
              </span>
            )}
            {request.waiting_pool_deadline && request.my_commitment_level === 'provisional' && (
              <ResponseCountdown
                deadline={request.waiting_pool_deadline}
                variant="badge"
                size="sm"
                label="Wartepool"
              />
            )}
            {request.is_partially_filled && (
              <span className="px-2 py-0.5 text-xs rounded-full bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200 shrink-0">
                {(request.lessons_accepted ?? 0)}/{request.lesson_count} angenommen
              </span>
            )}
          </div>
        </div>

        {/* Konzept A (IPB-Vereinheitlichung): Finanz-Wahl als Band oben —
            gleiche Position und Optik wie auf «Anfragen von mir», einheitliche
            Kurzlabels IPB / Auszahlung. */}
        {showFinanceChoice && request.finance_school_default && (
          <div className="mb-2">
            <FinanceChoiceRow
              groupName={`finance-payout-${request.id}`}
              title="Vergütung:"
              options={[
                { value: 'lesson_account', label: 'IPB' },
                { value: 'paid_out', label: 'Auszahlung' },
              ]}
              personalValue={financePersonal}
              schoolDefault={request.finance_school_default}
              canEdit={request.finance_can_edit === true}
              lockReason={request.finance_lock_reason ?? null}
              bindingAt={request.finance_binding_at}
              saving={financeSaving}
              error={financeError}
              onSwitch={handleFinanceSwitch}
            />
          </div>
        )}

        {/* Konzept A: Prep-Band an derselben Stelle — Badge + Aktionen wie auf
            «Anfragen von mir» (keine Header-Buttons mehr). */}
        {showPrepBand && (
          <div className="mb-2 flex items-center gap-2 flex-wrap text-xs bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded px-2.5 py-1.5">
            <span className="font-medium text-purple-800 dark:text-purple-200">Prep:</span>
            {request.prep_summary?.has_open_prep_request ? (
              <span className="px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-200 rounded-full">
                angefragt
              </span>
            ) : request.prep_summary?.has_prep ? (
              <span className="px-1.5 py-0.5 text-xs rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                {request.prep_summary.lessons_with_prep}/{request.prep_summary.total_lessons} Prep
              </span>
            ) : CANCELABLE_STATES.includes(request.status) ? (
              <span className="px-1.5 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-200">
                Kein Prep
              </span>
            ) : null}
            {showPrepRequestButton && (
              <button
                onClick={handlePrepRequestClick}
                className="px-2 py-1 text-xs text-purple-600 dark:text-purple-400 border border-purple-300 dark:border-purple-600 rounded hover:bg-purple-100 dark:hover:bg-purple-900/30 font-medium"
              >
                Prep anfordern
              </button>
            )}
            {showPrepViewButton && (
              <button
                onClick={() => navigate(getPrepPagePath(request.id, 'incoming'))}
                className="px-2 py-1 text-xs text-purple-600 dark:text-purple-400 border border-purple-300 dark:border-purple-600 rounded hover:bg-purple-100 dark:hover:bg-purple-900/30 font-medium"
              >
                Prep öffnen
              </button>
            )}
          </div>
        )}

        {/* Body: Single-lesson or Multi-lesson table */}
        {request.lesson_count === 1 ? (
          <>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                  {request.subject_name || '-'} • {request.klass_name || '-'}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {request.time_slot_name || '-'} •{' '}
                  {request.date
                    ? new Date(request.date).toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'short' })
                    : 'Datum offen'}
                  {request.is_multi_day && request.date_to && (
                    <> — {new Date(request.date_to).toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'short' })}</>
                  )}
                  {' '}• {request.lesson_count} Lektion{request.lesson_count !== 1 ? 'en' : ''}
                </div>
              </div>
              {shouldLoadSingleLesson && visibleLessons.length > 0 && onCancelLesson && (() => {
                const lesson = visibleLessons[0]!
                if (!isLessonCancellable(lesson)) return null
                const state = lesson.assignment_state || lesson.status
                return (
                  <button
                    onClick={() => onCancelLesson(request, lesson.id)}
                    className="shrink-0 p-1 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition"
                    title={state === 'waiting'
                      ? 'Aus dem Wartepool stornieren'
                      : 'Lektion stornieren (muss bestätigt werden)'}
                    aria-label="Lektion stornieren"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )
              })()}
            </div>
          </>
        ) : (
          <>
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              {request.date
                ? new Date(request.date).toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'short' })
                : 'Datum offen'}
              {request.is_multi_day && request.date_to && (
                <> — {new Date(request.date_to).toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'short' })}</>
              )}
              {' '}• {visibleLessons.length > 0 ? visibleLessons.length : request.lesson_count} Lektion{(visibleLessons.length > 0 ? visibleLessons.length : request.lesson_count) !== 1 ? 'en' : ''}
            </div>
            {isLoadingLessons ? (
              <div className="animate-pulse space-y-2">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
              </div>
            ) : visibleLessons.length > 0 ? (
              <div className="overflow-x-auto">
                {ganztagDates.length > 0 && isActionable && !isBrockenOffer && (
                  <div className="mb-2 space-y-1">
                    {ganztagDates.map(d => (
                      <label key={d} className="flex items-center gap-1.5 text-xs">
                        <input
                          type="checkbox"
                          checked={!!selectedDays[d]}
                          onChange={() => setSelectedDays(prev => ({ ...prev, [d]: !prev[d] }))}
                          disabled={isSubmittingLessons}
                        />
                        <span className="font-medium text-gray-700 dark:text-gray-200 min-w-[5.5rem]">
                          {new Date(d).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' })}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleDeclineDay(d)}
                          disabled={isSubmittingLessons}
                          className="px-2 py-0.5 text-red-700 dark:text-red-300 border border-red-300 dark:border-red-600 rounded hover:bg-red-50 disabled:opacity-50"
                        >
                          Ablehnen
                        </button>
                      </label>
                    ))}
                    <button
                      type="button"
                      onClick={handleUebernehmenSelectedDays}
                      disabled={isSubmittingLessons || !Object.values(selectedDays).some(Boolean)}
                      className="px-2 py-0.5 text-green-700 dark:text-green-300 border border-green-300 dark:border-green-600 rounded hover:bg-green-50 disabled:opacity-50"
                    >
                      Übernehmen
                    </button>
                    <p className="text-[10px] text-gray-400 dark:text-gray-500">
                      Nicht angekreuzte Tage bleiben offen.
                    </p>
                  </div>
                )}
                <table className="w-full text-xs" aria-label={`Details für ${visibleLessons.length} Lektionen`}>
                  <thead className="text-[10px] text-gray-500 dark:text-gray-400 uppercase border-b dark:border-gray-700">
                    <tr>
                      <th scope="col" className="pb-1 text-left font-medium">Datum</th>
                      <th scope="col" className="pb-1 text-left font-medium">Zeit</th>
                      <th scope="col" className="pb-1 text-left font-medium">Fach</th>
                      <th scope="col" className="pb-1 text-left font-medium">Klasse</th>
                      <th scope="col" className="pb-1 text-left font-medium">Raum</th>
                      <th scope="col" className="pb-1 text-right font-medium">Aktion</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {visibleLessons.map((lesson, index) => {
                      const style = getLessonRowStyle(
                        lesson.assignment_state,
                        lesson.assigned_teacher_id,
                        user?.teacher_id,
                      )
                      const { firstOfDay, dayIndex, isDayBreak } = lessonDayGroup(visibleLessons, index)
                      const dateLabel = lesson.date ? new Date(lesson.date).toLocaleDateString('de-DE', {
                        weekday: 'short', day: 'numeric', month: 'numeric',
                      }) : '-'
                      const room = substitutionRoomLabel(lesson, 'viewer', user?.teacher_id)
                      return (
                      <tr key={lesson.id} className={`${style.row} ${lessonDayRowClass(dayIndex, isDayBreak)}`}>
                        <td className={`py-1.5 ${style.text} ${lessonDayDateCellClass(dayIndex)}`}>
                          {firstOfDay ? dateLabel : <span className="sr-only">{dateLabel}</span>}
                        </td>
                        <td className={`py-1.5 ${style.text}`}>
                          {lesson.time_slot_name || '-'}
                        </td>
                        <td className={`py-1.5 font-medium ${style.text}`}>
                          {lesson.subject_name || '-'}
                        </td>
                        <td className={`py-1.5 ${style.text}`}>
                          {lesson.klass_name || '-'}
                        </td>
                        <td className={`py-1.5 ${style.text}`}>
                          {room || ''}
                        </td>
                        <td className="py-1.5 text-right">
                          {renderLessonStatus(lesson)}
                        </td>
                      </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-xs text-gray-500 dark:text-gray-400 italic">
                {request.lesson_count} Lektionen (Details nicht verfügbar)
              </div>
            )}
          </>
        )}

        {/* Cascade preview / indicator */}
        {cascadePreview && (cascadePreview.current_teachers.length > 0 || cascadePreview.next_teachers.length > 0) ? (
          <PriorityCascadeIndicator
            teachers={[]}
            cascadeStatus={request.cascade_status}
            requestMode={request.request_mode}
            requestFlow={request.request_flow}
            preview={cascadePreview}
            maxBadges={request.max_visible_cascade_badges ?? 0}
            isResolved={['accepted', 'filled'].includes(request.status) || request.already_covered}
          />
        ) : request.cascade_steps && request.cascade_steps.length > 0 ? (
          <CascadeIndicator
            steps={request.cascade_steps}
            viewerRank={request.viewer_rank}
            viewerIsLastRemaining={request.viewer_is_last_remaining}
          />
        ) : null}

        {(request.is_direct_assignment || directLessonIds.size > 0) && (
          <div className="mt-2 text-xs text-gray-500 italic">
            Direkt zugewiesen
          </div>
        )}
        {request.already_covered && (
          <div className="mt-2 text-xs text-gray-500 italic">
            Diese Lektionen wurden bereits von einer anderen Lehrperson übernommen.
          </div>
        )}

        {/* Prep Request Dialog */}
        {showPrepDialog && (
          <div className="mt-3 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
            {takenOverLessonIds.length > 0 && (
              <p className="text-xs text-purple-800 dark:text-purple-200 mb-2">
                Prep wird für {takenOverLessonIds.length} übernommene Lektion{takenOverLessonIds.length === 1 ? '' : 'en'} angefragt.
              </p>
            )}
            <textarea
              value={prepReason}
              onChange={(e) => setPrepReason(e.target.value)}
              placeholder="Grund (optional)"
              className="w-full p-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
              rows={2}
            />
            <div className="flex gap-2 mt-2">
              <button
                onClick={handleSubmitPrepRequest}
                disabled={isRequestingPrep}
                className="px-3 py-1 text-sm bg-purple-500 text-white rounded hover:bg-purple-600 disabled:opacity-50"
              >
                {isRequestingPrep ? 'Anfordern...' : 'Anfordern'}
              </button>
              <button
                onClick={() => {
                  setShowPrepDialog(false)
                  setPrepReason('')
                }}
                className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                Abbrechen
              </button>
            </div>
          </div>
        )}

        {/* Error display */}
        {lessonSubmitError && (
          <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded text-xs flex items-center justify-between">
            <span>{lessonSubmitError}</span>
            <button onClick={() => setLessonSubmitError(null)} className="ml-2 underline shrink-0">×</button>
          </div>
        )}

        {/* Footer */}
        {showFooterActions && (
          <div className="flex items-center justify-between flex-wrap gap-2 mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-2 flex-wrap">
              {request.viewer_answer_deadline && !hardTerminal && (
                <ResponseCountdown
                  deadline={request.viewer_answer_deadline}
                  variant="badge"
                  size="sm"
                  lateAnswerAllowed
                />
              )}
              {request.next_answer_deadline && !hardTerminal && (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Nächste Antwortfrist:</span>
                  <ResponseCountdown
                    deadline={request.next_answer_deadline}
                    variant="badge"
                    size="sm"
                    label="Nächste Antwortfrist"
                  />
                </div>
              )}
            </div>

            <div className="flex gap-1.5 flex-wrap items-center">
              {/* Multi-lesson actionable: bulk + submit */}
              {isMultiLesson && isActionable && respondableLessons.length > 0 && (
                <>
                  {!isBrockenOffer && (
                  <button
                    onClick={handleAcceptAllOffered}
                    disabled={isSubmittingLessons}
                    className="px-2 py-1 text-xs text-green-700 dark:text-green-300 border border-green-300 dark:border-green-600 rounded hover:bg-green-50 dark:hover:bg-green-900/30 disabled:opacity-50 font-medium"
                  >
                    Alle übernehmen
                  </button>
                  )}
                  <button
                    onClick={() => setAllDecisions('interested')}
                    disabled={isSubmittingLessons}
                    className="px-2 py-1 text-xs text-blue-700 dark:text-blue-300 border border-blue-300 dark:border-blue-600 rounded hover:bg-blue-50 dark:hover:bg-blue-900/30 disabled:opacity-50"
                  >
                    Alle interessiert
                  </button>
                  <button
                    onClick={isPackageOffer
                      ? () => submitLessonResponses(respondableLessons.map(l => ({
                          lesson_request_id: l.id,
                          response: 'decline',
                        })))
                      : () => setAllDecisions('decline')}
                    disabled={isSubmittingLessons}
                    className="px-2 py-1 text-xs text-red-700 dark:text-red-300 border border-red-300 dark:border-red-600 rounded hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50"
                  >
                    Alle ablehnen
                  </button>
                  <button
                    onClick={handleLessonsSubmit}
                    disabled={pendingDecisionsCount === 0 || isSubmittingLessons}
                    className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium dark:bg-blue-600 dark:hover:bg-blue-700"
                  >
                    {isSubmittingLessons
                      ? '...'
                      : pendingDecisionsCount > 0
                        ? `Auswahl bestätigen (${pendingDecisionsCount})`
                        : 'Auswahl bestätigen'}
                  </button>
                </>
              )}

              {/* Single-lesson actionable: toggle */}
              {!isMultiLesson && isActionable && (
                <>
                  <button
                    type="button"
                    onClick={() => handleSingleRespond('accept')}
                    disabled={isRespondingSingle}
                    className="px-2 py-1 text-xs text-green-700 dark:text-green-300 border border-green-300 dark:border-green-600 rounded hover:bg-green-50 disabled:opacity-50 font-medium"
                  >
                    Übernehmen
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSingleRespond('interested')}
                    disabled={isRespondingSingle}
                    className="px-2 py-1 text-xs text-blue-700 dark:text-blue-300 border border-blue-300 dark:border-blue-600 rounded hover:bg-blue-50 disabled:opacity-50"
                  >
                    Interessiert
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSingleRespond('decline')}
                    disabled={isRespondingSingle}
                    className="px-2 py-1 text-xs text-red-700 dark:text-red-300 border border-red-300 dark:border-red-600 rounded hover:bg-red-50 disabled:opacity-50"
                  >
                    Ablehnen
                  </button>
                </>
              )}

              {/* Accepted: storno — not for Admin-Stellvertretung directs */}
              {request.status !== 'cancelled' && displayStatus !== 'declined' && onCancel && directLessonIds.size === 0 && (
                <div className="relative group">
                  <button
                    onClick={() => onCancel(request)}
                    className="px-2 py-1 text-xs text-red-600 border border-red-300 rounded hover:bg-red-50 disabled:opacity-50 dark:text-red-400 dark:border-red-600 dark:hover:bg-red-900/30 transition"
                    title="Anfrage stornieren"
                    aria-label="Anfrage stornieren"
                  >
                    Stornieren
                  </button>
                  {hrOn && hasHrRelevantLessons && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-yellow-50 border border-yellow-300 rounded shadow-lg text-xs text-yellow-900 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 dark:bg-yellow-900/90 dark:border-yellow-600 dark:text-yellow-100">
                      <div className="font-semibold mb-1">⚠️ HR-Warnhinweis</div>
                      <div>Diese Anfrage enthält Lektionen die an das HR für die Vergütung gehen würden.</div>
                      <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px">
                        <div className="border-4 border-transparent border-t-yellow-300 dark:border-t-yellow-600"></div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Message */}
              {onSendMessage && (
                <button
                  onClick={() => onSendMessage(request.id)}
                  className="px-2 py-1 text-xs text-blue-600 dark:text-blue-400 border border-blue-300 dark:border-blue-600 rounded hover:bg-blue-50 dark:hover:bg-blue-900/30"
                >
                  Nachricht
                </button>
              )}

              <CascadeLegendTrigger />
            </div>
          </div>
        )}
    </div>
  )
})
